import { createContext, useContext, useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { observer } from '@react';

import type { ScopeContext, ScopeFilter } from '@/features/scope/types';
import {
  DEFAULT_SCOPE,
  getScopeFilter as computeScopeFilter,
  isScopeEqual,
  parseScopeFromUrl,
  serializeScopeToParams,
} from '@/features/scope/logic';

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------

export interface ScopeContextValue {
  /** Current active scope */
  scope: ScopeContext;
  /** Update the active scope (also syncs URL params) */
  setScope: (next: ScopeContext) => void;
  /** Derive API filter params from the current scope */
  getScopeFilter: () => ScopeFilter;
}

/**
 * Exported so a page can re-provide it where its own tree does not reach — the
 * right rail is a sibling of the panes, and settings shown there still have to
 * read the scope the page is in.
 */
export const ScopeCtx = createContext<ScopeContextValue | null>(null);

// ---------------------------------------------------------------------------
// URL search-params subscription (no router dependency)
// ---------------------------------------------------------------------------

function subscribeToSearchParams(callback: () => void): () => void {
  window.addEventListener('popstate', callback);
  return () => window.removeEventListener('popstate', callback);
}

let _cachedSearch = '';
let _cachedParams = new URLSearchParams('');

function getSearchParamsSnapshot(): URLSearchParams {
  const current = window.location.search;
  if (current !== _cachedSearch) {
    _cachedSearch = current;
    _cachedParams = new URLSearchParams(current);
  }
  return _cachedParams;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ScopeProviderProps {
  children: ReactNode;
  /** Optional default scope override (takes precedence when URL has no scope params) */
  defaultScope?: ScopeContext;
}

export const ScopeProvider = observer(({ children, defaultScope }: ScopeProviderProps) => {
  const fallback = defaultScope ?? DEFAULT_SCOPE;
  const prevScopeRef = useRef<ScopeContext>(fallback);

  // Subscribe to browser URL search-param changes (back/forward navigation)
  const searchParams = useSyncExternalStore(subscribeToSearchParams, getSearchParamsSnapshot);

  const scope = useMemo(() => {
    const parsed = parseScopeFromUrl(searchParams);
    // If the URL has no scope params, use the fallback
    if (parsed.scopeType === 'all' && !searchParams.has('scope')) {
      return prevScopeRef.current.scopeType === 'all' ? fallback : prevScopeRef.current;
    }
    prevScopeRef.current = parsed;
    return parsed;
  }, [searchParams, fallback]);

  const setScope = useCallback(
    (next: ScopeContext) => {
      if (isScopeEqual(next, prevScopeRef.current)) {
        return;
      }

      prevScopeRef.current = next;

      // Build new URL preserving non-scope params
      const current = new URLSearchParams(window.location.search);

      // Remove old scope params
      current.delete('scope');
      current.delete('scope_id');
      current.delete('scope_name');
      current.delete('scope_entity_type');

      // Apply new scope params
      const nextParams = serializeScopeToParams(next);
      for (const [key, value] of Object.entries(nextParams)) {
        current.set(key, value);
      }

      const qs = current.toString();
      const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}`;

      // Use replaceState to avoid polluting history
      window.history.replaceState(window.history.state, '', newUrl);

      // Dispatch popstate so useSyncExternalStore picks up the change
      window.dispatchEvent(new PopStateEvent('popstate'));
    },
    [],
  );

  const getScopeFilter = useCallback(() => computeScopeFilter(scope), [scope]);

  const value = useMemo<ScopeContextValue>(
    () => ({ scope, setScope, getScopeFilter }),
    [scope, setScope, getScopeFilter],
  );

  return <ScopeCtx.Provider value={value}>{children}</ScopeCtx.Provider>;
});

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useScope(): ScopeContextValue {
  const ctx = useContext(ScopeCtx);
  if (!ctx) {
    throw new Error('useScope must be used within a <ScopeProvider>');
  }
  return ctx;
}
